import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Download } from "lucide-react";
import { api, label } from "../lib/api";
import { PageHeader, Select, Spinner } from "../components/bits";
import { ContributionsTable } from "../components/ContributionsTable";

const STATUSES = ["paid", "pending_verification", "partially_paid", "outstanding", "overdue", "rejected"];
const TYPES = ["rotation", "savings", "special", "general", "membership_fee"];

export default function Contributions() {
  const [sp] = useSearchParams();
  const [groups, setGroups] = useState([]);
  const [rows, setRows] = useState(null);
  const [members, setMembers] = useState([]);
  const [f, setF] = useState({ group_id: sp.get("group") || "", status: "", type: "", member_id: "", date_from: "", date_to: "", mine: sp.get("mine") === "1" });

  useEffect(() => { api.get("/groups").then((r) => setGroups(r.data)); }, []);
  useEffect(() => {
    if (!f.group_id) { setMembers([]); return; }
    api.get(`/groups/${f.group_id}/members`).then((r) => setMembers(r.data)).catch(() => setMembers([]));
  }, [f.group_id]);
  const load = () => {
    const params = Object.fromEntries(Object.entries(f).filter(([, v]) => v !== "" && v !== false));
    api.get("/contributions", { params }).then((r) => setRows(r.data));
  };
  useEffect(load, [f]); // eslint-disable-line react-hooks/exhaustive-deps

  const roleFor = (oid) => groups.find((g) => g.id === oid)?.my_role;
  const exportCsv = () => window.open(`${process.env.REACT_APP_BACKEND_URL}/api/reports/contributions?format=csv${f.group_id ? `&group_id=${f.group_id}` : ""}&auth=${localStorage.getItem("nini_token")}`, "_blank");
  const counts = rows ? STATUSES.reduce((a, s) => ({ ...a, [s]: rows.filter((r) => r.status === s).length }), {}) : {};

  return (
    <div data-testid="contributions-page">
      <PageHeader title="Contributions" subtitle="Track and manage all contributions across your groups." action={<button className="btn-secondary" onClick={exportCsv} data-testid="contributions-export-btn"><Download size={16} /> Export CSV</button>} />
      <div className="card p-4 mb-6 grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3" data-testid="contribution-filters">
        <Select testId="filter-group" value={f.group_id} onChange={(v) => setF({ ...f, group_id: v, member_id: "" })} placeholder="All groups" options={groups.map((g) => [g.id, g.name])} />
        <Select testId="filter-status" value={f.status} onChange={(v) => setF({ ...f, status: v })} placeholder="All statuses" options={STATUSES.map((s) => [s, `${label(s)}${counts[s] !== undefined && !f.status ? ` (${counts[s]})` : ""}`])} />
        <Select testId="filter-type" value={f.type} onChange={(v) => setF({ ...f, type: v })} placeholder="All types" options={TYPES} />
        <Select testId="filter-member" value={f.member_id} onChange={(v) => setF({ ...f, member_id: v })} placeholder={f.group_id ? "All members" : "Select a group first"} options={members.map((m) => [m.user_id, m.user?.name])} />
        <input data-testid="filter-date-from" type="date" className="field" value={f.date_from} onChange={(e) => setF({ ...f, date_from: e.target.value })} />
        <input data-testid="filter-date-to" type="date" className="field" value={f.date_to} onChange={(e) => setF({ ...f, date_to: e.target.value })} />
        <label className="col-span-2 md:col-span-3 lg:col-span-6 inline-flex items-center gap-2 text-sm text-nini-text"><input type="checkbox" data-testid="filter-mine" checked={f.mine} onChange={(e) => setF({ ...f, mine: e.target.checked })} className="accent-nini-green h-4 w-4" /> Only show my contributions</label>
      </div>
      {rows === null ? <Spinner /> : <ContributionsTable rows={rows} roleFor={roleFor} onChange={load} />}
    </div>
  );
}
